import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { FormContainer } from '@/components/form/form-container'
import { parseCustomFormHostname } from '@/lib/api'
import { useFormConfigByCustomDomain } from '@/hooks/use-form-config'

// Search params schema for single-page mode
const searchParamsSchema = z.object({
  long: z.coerce.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/')({
  component: IndexPage,
  validateSearch: (search) => searchParamsSchema.parse(search),
})

function IndexPage() {
  const customHost = parseCustomFormHostname()
  if (customHost) {
    return <CustomDomainFormPage host={customHost} />
  }

  // Otherwise show the missing organization message
  return <MissingOrganizationPage />
}

function CustomDomainFormPage({ host }: { host: string }) {
  const { long: singlePage } = Route.useSearch()
  const { config, isLoading, error, refetch } = useFormConfigByCustomDomain(host)

  if (isLoading) {
    return <FormLoadingSkeleton />
  }

  if (error || !config?.organizationId) {
    return (
      <FormErrorState
        error={error || 'The form configuration could not be loaded. Please try again.'}
        onRetry={refetch}
      />
    )
  }

  return (
    <div className="min-h-screen flex items-start bg-transparent p-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-2xl mx-auto"
      >
        <FormContainer
          organizationId={config.organizationId}
          config={config}
          singlePage={singlePage}
        />
      </motion.div>
    </div>
  )
}

function FormLoadingSkeleton() {
  return (
    <div className="min-h-screen flex items-start bg-transparent p-4">
      <div className="w-full max-w-2xl mx-auto">
        <div className="rounded-xl border border-border/50 bg-card/95 backdrop-blur-sm shadow-lg shadow-black/[0.03]">
          <div className="border-b border-border/50 bg-muted/30 px-6 py-5 sm:px-8 rounded-t-xl">
            <div className="relative">
              <div className="absolute top-5 left-0 right-0 h-[2px] bg-border mx-10" />
              <div className="relative flex justify-between">
                {[
                  { label: 'Event Details', width: 'w-24' },
                  { label: 'Event Information', width: 'w-32' },
                  { label: 'Add-ons', width: 'w-20' },
                  { label: 'Contact', width: 'w-20' },
                ].map((step, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <Skeleton className={`h-3 ${step.width} hidden sm:block`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div className="min-h-[320px] space-y-8">
              <div className="space-y-1">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-72" />
              </div>
              <div className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2 sm:items-start">
                  <div className="flex flex-col space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-9 w-full" />
                    <div className="min-h-[20px]" />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-9 w-full" />
                    <div className="min-h-[20px]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-4 w-64" />
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-border/50 bg-muted/20 px-6 py-4 sm:px-8 rounded-b-xl">
            <div className="flex items-center justify-between gap-4">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FormErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex min-h-[400px] items-center justify-center bg-transparent p-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="rounded-xl border border-destructive/20 bg-card/95 backdrop-blur-sm shadow-lg p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            Unable to Load Form
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button onClick={onRetry} variant="outline" className="mt-6 gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </motion.div>
    </div>
  )
}

function MissingOrganizationPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <div className="text-destructive text-lg font-medium">
            Missing Organization ID
          </div>
          <p className="text-muted-foreground">
            Please use a valid form URL that includes an organization ID.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
